/**
 * @title isEmpty
 * @description 是无效值 undefined , null, NaN
 * @param value {unknown} 待值
 * @returns {boolean}
 */
function isEmpty(value) {
    return value === undefined || value === null || value !== value;
}
/**
 * @title isNoEmpty<T>
 * @description 不是是无效值 undefined , null, NaN
 * @param {unknown} value
 * @returns {boolean}
 * @version 0.3.0
 */
function isNoEmpty(value) {
    return value !== undefined && value !== null && value === value;
}
/**
 * @title isUndefined
 * @param {unknown} value
 * @returns {boolean}
 */
function isUndefined(value) {
    return value === undefined;
}
/**
 * @title isNaN
 * @param {unknown} value
 * @returns {boolean}
 */
const isNaN$1 = Number.isNaN;

/**
 * @title isArray
 * @description 是数组
 * @param value {unknown}
 * @returns {boolean}
 */
function isArray(list) {
    return Array.isArray(list);
}
/**
 * @title isEffectArray
 * @description 是有效数组
 * @param list {unknown}
 * @returns {boolean}
 */
function isEffectArray(list) {
    if (isEmpty(list))
        return false;
    return isArray(list) && list.length > 0;
}

/**
 * @title isIterator
 * @description 是iterator
 * @param {unknown} value
 * @returns {boolean}
 */
function isIterator(value) {
    if (typeof value !== 'object' || isEmpty(value))
        return false;
    return /Iterator\]$/.test(value.toString());
}

function type(param) {
    try {
        if (isIterator(param))
            return 'Iterator';
        const result = Object.prototype.toString
            .call(param)
            .match(/\[object (\w+)\]/)[1];
        if (result === 'Number' && isNaN(param))
            return 'NaN';
        return result;
    }
    catch (error) {
        return 'Undefined';
    }
}

/**
 * @title isFunction
 * @description 是函数
 * @param {unknown} value
 * @param {boolean} [strict=false] 严格模式
 * @returns {boolean}
 */
function isFunction(value, strict = false) {
    if (strict) {
        return typeof value === 'function';
    }
    return type(value) === 'Function';
}
/**
 * @title isAsyncFunction
 * @description 是异步函数
 * @param {unknown} value
 * @returns {boolean}
 */
function isAsyncFunction(value) {
    return type(value) === 'AsyncFunction';
}
const INFINITY$1 = Infinity; // 无限

/**
 * @title isObject
 * @description 是Object
 * @param {unknown} value
 * @returns {boolean}
 */
function isObject(value) {
    return value !== null && typeof value === 'object' && type(value) === 'Object';
}
/**
 * @title isEffectObject
 * @description 是有效Object(非空)
 * @param {unknown} value
 * @returns {boolean}
 */
function isEffectObject(value) {
    if (isEmpty(value))
        return false;
    return isObject(value) && Reflect.ownKeys(value).length !== 0;
}

/**
 * @title isString
 * @description 是字符串
 * @param {unknown} value
 * @returns {boolean}
 */
function isString(value) {
    return typeof value === 'string';
}

/**
 * @title isNumber
 * @description 是数字
 * @param {unknown} value
 * @returns {boolean}
 */
const isNumber = (value) => {
    return typeof value === 'number' && type(value) === 'Number';
};
/**
 * @title isEffectNumber
 * @description 是js的有效区间的数, 非number类型都为false
 * @param {unknown} value
 * @returns {boolean}
 */
function isEffectNumber(value) {
    if (type(value) === 'Number') {
        if (value === INFINITY$1 || value === -INFINITY$1)
            return false;
        return true;
    }
    return false;
}
/**
 * @title isFloat
 * @description 判断数是浮点型
 * @param {unknown} value 待检测的数据类型
 * @returns {boolean}
 */
function isFloat$1(value) {
    if (!isNumber(value))
        return false;
    return (value % 1) !== 0;
}
/**
 * @title likeNumber
 * @description 是数字/数字字符串
 * @support: Number, NumberString
 * @unsupported: Infinity, Function
 * @param {unknown} value
 * @returns {boolean}
 * @version 0.1.0
 */
function likeNumber(value) {
    if (isNumber(value))
        return true;
    if (isString(value))
        value = value.trim();
    if (isEmpty(value)
        || isArray(value)
        || value === '')
        return false;
    if (Number.isNaN(Number(value)) === false) {
        return true;
    }
    return false;
}

/**
 * @title isDate
 * @description 检查日期是否有效
 * @param {unknown} date 待判断日期
 * @returns {boolean}
 * @version 0.1.0
 */
function isDate(date) {
    return type(date) === 'Date';
}
/**
 * @title isTime
 * @description 检查日期是否有效, 时间戳也为有效时间(13位/10位)
 * @param {unknown} time 待判断日期
 * @returns {boolean}
 * @version 0.1.0
 */
function isTime(time) {
    if (isNumber(time)) {
        if (time.toString().length === 13)
            return true;
        if (time.toString().length === 10)
            return true;
    }
    return time instanceof Date && !isNaN(time.getTime());
}
/**
 * @title isBoolean
 * @description 是布尔值
 * @param {unknown} val
 * @returns {boolean}
 */
const isBoolean = (val) => typeof val === 'boolean';

/**
 * @title isRegExp
 * @description 判断是否为正则表达式
 * @param {unknown} value
 * @returns {boolean}
 */
function isRegExp(value) {
    return type(value) === 'RegExp';
}

/**
 * @title isSymbol
 * @param {unknown} value
 * @returns {boolean}
 */
function isSymbol(value) {
    return typeof value === 'symbol';
}

/**
 * @title isSet
 * @param {unknown} value
 * @returns {boolean}
 */
function isSet(value) {
    return type(value) === 'Set';
}

/**
 * @title isMap
 * @param {unknown} value
 * @returns {boolean}
 */
function isMap(value) {
    return type(value) === 'Map';
}

const MAX_VALUES_NUMBER = 1.7976931348623157e+308;
const MIN_VALUES_NUMBER = -1.7976931348623157e+308;
const INFINITY = Infinity; // 无限

/**
 * @title toNumber
 * @description 将值转换为Number, 不可以正确装换的值, 均返回0
 * @param {unknown} num 待转换的数值
 * @returns {number}
 */
function toNumber(num) {
    if (isString(num) || isNumber(num)) {
        const value = isString(num) ? Number(num) : num;
        if (isEffectNumber(value)) {
            if (value === INFINITY)
                return MAX_VALUES_NUMBER;
            if (value === -INFINITY)
                return MIN_VALUES_NUMBER;
            return value;
        }
    }
    return 0;
}
/**
 * @title toFloat
 * @description 转换为指定位数的浮点数
 * @param {number} num 数字
 * @param {number} fixed 小数点位数
 * @returns {number}
 */
function toFloat$1(num, fixed = 1) {
    return Number(num.toFixed(fixed));
}
/**
 * @title toThousands
 * @description 数字每千位加逗号
 * @param {string|number} num
 * @returns {string}
 */
function toThousands(num) {
    return num.toString().replace(/(\d)(?=(\d{3})+$)/g, '$1,');
}

/**
 * @title spLength
 * @description 指定长度
 * @param {number|string} value
 * @param {number} [min=0]
 * @param {number} max
 * @returns {string}
 */
function spLength(value, min = 0, max) {
    const tmpValue = isNumber(value) ? value.toString() : value;
    if (!/^[0-9]*$/.test(tmpValue))
        return new Array(min).fill(0).join('');
    const len = tmpValue.length;
    if (len > max)
        return tmpValue.slice(len - max);
    if (len < min)
        return new Array(min - len).fill(0).join('') + tmpValue;
    return tmpValue;
}
/**
 * @title getDecimal
 * @description 获取小数点位数
 * @param {number} num
 * @returns {number}
 */
function getDecimal(num) {
    if (!isFloat$1(num))
        return 0;
    return num.toString().split('.')[1].length;
}
/**
 * @title clamp
 * @description 限制在lower和upper之间
 * @param {number} num 待限制的值
 * @param {number} lower 下限
 * @param {number} upper 上限
 * @returns 返回被限制的值
 */
function clamp(num, lower = -INFINITY, upper = INFINITY) {
    if (num < lower)
        return lower;
    if (num > upper)
        return upper;
    return num;
}
/**
 * @title inRange
 * @description 判断是否在该范围
 * @param {number} num 要检查的值
 * @param {number} start=0 开始范围
 * @param {number} end 结束范围(包含该值)
 * @returns {boolean}
 */
function inRange(num, start = 0, end = MAX_VALUES_NUMBER) {
    if (end < start)
        return false;
    if (num > end)
        return false;
    if (num < start)
        return false;
    return true;
}
/**
 * @title between
 * @description 判断值是否在两值之间
 * @param {number} num 待判断值
 * @param {number} start=0 起始值
 * @param {number} [end] 结束值(不包含该值)
 * @returns {boolean}
 */
function between(num, start = 0, end = MAX_VALUES_NUMBER) {
    if (end < start)
        return false;
    if (num >= end)
        return false;
    if (num < start)
        return false;
    return true;
}

/**
 * @title padNumber
 * @description 填充数字
 * @param {number|string} value 推荐使用字符串，数字小数点最后的零可能会导致结果不符合预期
 * @param {number|`${number|''}.${number|''}`} length length>0 表整数位保留部分， length<0 表小数点保留位： '2.3': 表2位整数， 3位小数
 * @returns {string}
 */
function padNumber(value, length, strict = false) {
    const target = isNumber(value) ? String(value) : value;
    const [leftNum, rightNum] = target.split('.');
    const len = leftNum ? leftNum.length : 0;
    if (isNumber(length)) {
        if (length > 0) {
            if (length > len) {
                return new Array(length + 1 - len).join('0') + target;
            }
            if (strict) {
                return new Array(length + 1).join('9');
            }
            return target;
        }
        if (length < 0) {
            return Number(target).toFixed(-length);
        }
        return target;
    }
    if (isString(length)) {
        const [left, right] = length.split('.').map((v) => Math.abs(Number(v)));
        let newTarget = leftNum;
        if (!isNaN$1(left) && left > 0) {
            if (left > len) {
                newTarget = new Array(left + 1 - len).join('0') + leftNum;
            }
            else if (strict) {
                newTarget = new Array(left + 1).join('9');
            }
        }
        if (!isNaN$1(right) && right > 0) {
            if (strict || !rightNum || right > rightNum.length) {
                newTarget =
                    newTarget +
                        Number(rightNum || 0)
                            .toFixed(right)
                            .toString()
                            .slice(1);
            }
            else {
                newTarget = newTarget + '.' + rightNum;
            }
        }
        return newTarget;
    }
    return target;
}

/**
 * @title reverseString
 * @description 反转字符串
 * @param {string} target
 * @return {string}
 */
function reverseString(target) {
    return target.split('').reverse().join('');
}

/**
 * @title toString
 * @description 任意类型均可转换为string
 * @param {unknown} value
 * @returns {string}
 * @lastUpdate: 2.2.1
 */
function toString(value) {
    if (isString(value))
        return value;
    if (isEmpty(value))
        return '';
    if ([
        'Function', 'AsyncFunction', 'GeneratorFunction',
        'Symbol', 'RegExp', 'Promise', 'Date',
        'Map', 'Set', 'WeakMap', 'WeakSet', 'BigInt'
    ].includes(type(value)))
        return value.toString();
    if (value === Infinity)
        return 'Infinity';
    if (value === -Infinity)
        return '-Infinity';
    return JSON.stringify(value);
}
/**
 * @title toStrings
 * @description 转换为字符串数组, 即数组的的项转换为数组
 * @param {unknown[]} values
 * @returns {string[]}
 */
function toStrings(values) {
    return values.map(i => toString(i));
}

/**
 * @title stringify
 * @description JSON.stringify 的二次封装, 原本很多类型返回undefined等值, 都会返回各有意义的值, value 为字符串类型不会再加多一对双引号
 * @param {unknown} value
 * @param {(number|string)[]|(this:any,key:string,value:any)=>any}[replacer]
 * @param {string|number} [space]
 * @returns {string}
 * @lastUpdate 2.2.1
 */
function stringify(value, replacer, space) {
    if (isEmpty(value)) {
        return String(value);
    }
    if (isObject(value) || isArray(value)) {
        return JSON.stringify(value, replacer, space);
    }
    if (isSymbol(value))
        return value.toString();
    return JSON.stringify(toString(value), replacer, space).replace(/^(")+|(")+$/g, '');
}
function parse(value, defaultValue) {
    try {
        if (isString(value)) {
            return JSON.parse(value);
        }
        return defaultValue;
    }
    catch (error) {
        return defaultValue;
    }
}

/**
 * @title hide
 * @description 隐藏指定位置的字符
 * @param {string} target 待替换子串
 * @param {number} [start=0] 开始位置
 * @param {number} [end=target.length] 结束位置
 * @returns {string}
 * @lastUpdate 2.2.1
 */
function hide(target, start = 0, end) {
    if (isEmpty(target))
        return '';
    if (type(target) !== 'String') {
        target = toString(target);
    }
    if (start <= 0)
        start = 0;
    if (!end || (end > target.length)) {
        end = target.length;
        if (start === 0)
            return target.replace(/./gi, '*');
    }
    const center_len = end - start;
    const end_len = (target.length - end) || 0;
    const reg = new RegExp(`(.{${start}})(.{${center_len}})(.{${end_len}})`, 'g');
    const result = reg.exec(target);
    if (result?.length === 4) {
        result[2] = result[2].replace(/./gi, '*');
        delete result[0];
        return result.join('');
    }
    return target.replace(/./gi, '*');
}

/**
 * @title vid
 * @description 获取虚拟id (时间戳)
 * @returns {string}
 */
const vid = () => {
    return new Date().getTime().toString();
};

/**
 * @title compareString
 * @description 比较字符串大小
 * @param {string} compare
 * @param {string} compared
 * @returns {boolean}
 */
function compareString(compare, compared) {
    if (isString(compare) && isString(compared)) {
        if (compare.length !== compared.length) {
            return compare.length > compared.length;
        }
        for (let i = 0; i < compare.length; i++) {
            if (compare.charCodeAt(i) > compared.charCodeAt(i)) {
                return true;
            }
            if (compare.charCodeAt(i) < compared.charCodeAt(i)) {
                return false;
            }
        }
    }
    return false;
}

/**
 * @title replaces
 * @description 同时定义多个replace的规则使用
 * @param {string} target
 * @param {{reg:RegExp|string,value:string}[]} regs
 * @returns {string}
 */
function replaces(target = '', regs = []) {
    for (let i = 0; i < regs.length; i++) {
        const { reg, value } = regs[i];
        target = target.replace(reg, value);
    }
    return target;
}

function pad(value) { }
function padStart(value) { }
function padEnd(value) { }

/**
 * @title toDate
 * @description 字符串装换成Date对象
 * @param {string} value 可以转换成时间的字符串
 * @returns {Date}
 */
function toDate(value) {
    // ios 不支持 YYYY-MM-DD hh:mm:ss
    if (/\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(value)) {
        value.replaceAll('-', '/');
    }
    return new Date(value);
}

/**
 * @title deadline
 * @description 倒计时
 * @param {Date} target {Date} 目标时间
 * @param {'year'|'mouth'|'day'|'hour'|'minute'|'second'|'timeStamp'} [timeKey]   指定倒计时单位
 * @param {Date} [now=new Date()] 起始时间
 * @returns {number}
 */
function deadline(target, timeKey = 'day', now = new Date()) {
    const surplusTimeStamp = target.getTime() - now.getTime();
    const surplusDay = Math.ceil(surplusTimeStamp / 86400000);
    switch (timeKey) {
        case 'year': return target.getFullYear() - now.getFullYear();
        case 'mouth': return (target.getFullYear() - now.getFullYear()) * 12 + (target.getDate() - now.getDate());
        case 'day': return surplusDay;
        case 'hour': return surplusDay * 24;
        case 'minute': return surplusDay * 1440;
        case 'second': return surplusDay * 8640086400;
        case 'timeStamp': return surplusDay;
        default: return surplusTimeStamp;
    }
}

const getOffsetToTimezone = (date, format = 'Z') => {
    const offsetMinutes = date.getTimezoneOffset();
    const offsetHours = offsetMinutes / 60;
    const sign = offsetHours > 0 ? '-' : '+';
    const absOffsetHours = Math.abs(offsetHours);
    const hours = Math.floor(absOffsetHours);
    const minutes = Math.floor((absOffsetHours - hours) * 60);
    if (format === 'ZZ')
        return sign + padNumber(hours, 2) + padNumber(minutes, 2);
    return sign + padNumber(hours, 2) + ':' + padNumber(minutes, 2);
};
/**
 * @title format
 * @description 时间格式化
 * @param {number|string|Date} [time=new Date()]  时间
 * @param {string} [pattern='YYYY-MM-DD'] 格式
 * @returns {string} 格式化后的数据
 * @eg | 符号 | 结果| 描述 |
|:----|:----|:----|
| YYYY	| 2022	| 4位数字的年份 |
| YY	|  1-14	| 2 位数字的年份 |
| M  MM |	1-12 |	月份数字 |
| D  DD |	1-31 |	日数(忽略大小写) |
| d |	0-6 |	一周的第几天， 0：星期天 |
| H  HH	| 0-23 |  24 小时制 |
| h  hh	| 1-12 |	12 小时制 |
| m  mm | 0-59 |	分钟|
| s  ss	| 0-59 |	秒钟|
| S |	0-9 |	毫秒，一位数
| SS |	00-99 |	毫秒，两位数
| SSS |	000-999 |	毫秒，三位数
| A | AM PM	|
| a | am pm |
| Z |	+05:00 |	UTC 的偏移量，±HH:mm
| ZZ |+0500	| UTC 的偏移量，±HHmm
 */
function format(time = new Date(), pattern = 'YYYY-MM-DD') {
    if (type(time) === 'Number') {
        if (time.toString().length === 10)
            time += '000';
    }
    const date = new Date(time);
    const year = date.getFullYear();
    // eslint-disable-next-line
    if (isNaN$1(year)) {
        return 'Invalid Date';
    }
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const week = date.getDay();
    const hour = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const milliseconds = date.getMilliseconds();
    const reg = /Y{4}|\[Y{4}\]|YY|\[YY\]|[DMHhmsZ]{1,2}|\[[DMHhmsZ]{1,2}\]|SSS|\[SSS\]|[AaXxd]|\[[AaXxd]\]|./gi;
    const patterns = pattern.match(reg) || [];
    const result = patterns
        .map((item) => {
        if (!item.match(reg))
            return item;
        if (item.match(/^\[.*?\]$/))
            return item.replace(/\[|\]/gi, '');
        if (item === 'YYYY')
            return padNumber(year, 4);
        if (item === 'YY')
            return padNumber(year % 100, 2);
        if (item === 'MM')
            return padNumber(month, 2);
        if (item === 'M')
            return month;
        if (item === 'DD')
            return padNumber(day, 2);
        if (item === 'D')
            return day;
        if (item === 'D')
            return week;
        if (item === 'HH')
            return padNumber(hour, 2);
        if (item === 'H')
            return hour;
        if (item === 'hh')
            return padNumber(hour % 12, 2);
        if (item === 'h')
            return hour % 12;
        if (item === 'mm')
            return padNumber(minutes, 2);
        if (item === 'm')
            return minutes;
        if (item === 'A')
            return hour >= 12 ? 'PM' : 'AM';
        if (item === 'a')
            return hour >= 12 ? 'pm' : 'am';
        if (item === 'ss')
            return padNumber(seconds, 2);
        if (item === 's')
            return seconds;
        if (item === 'SSS')
            return padNumber(milliseconds, 3);
        if (item === 'SS')
            return padNumber(Math.floor(milliseconds / 10), 2);
        if (item === 'S')
            return padNumber(Math.floor(milliseconds / 100), 1);
        if (item === 'ZZ')
            return getOffsetToTimezone(date, 'ZZ');
        if (item === 'Z')
            return getOffsetToTimezone(date);
        return item;
    })
        .join('');
    return result;
}

/**
 * @title timezone
 * @description 指定时区时间偏移量, 转换为目标时间
 * @param {LikeNumber} originOffset 当前时间的时区(number:时间偏移量|string:时区)
 * @param {LikeNumber} targetOffset 目标时间的时区(number:时间偏移量|string:时区)
 * @returns {(data:Date)=>Date}
 * @eg ```js
 const oDate = new Date('2022-12-11T07:58:07.945Z')
 const tDate = new Date('2022-12-11T15:58:07.945Z')
 timezone('1', 9 * 3600000)(oDate) ==> tDate
 ```
 */
function timezone(originOffset, targetOffset) {
    const originOffsetNum = isString(originOffset) ? toNumber(originOffset) * 3600000 : originOffset;
    const targetOffsetNum = isString(targetOffset) ? toNumber(targetOffset) * 3600000 : targetOffset;
    const offset = targetOffsetNum - originOffsetNum;
    return (date) => new Date(date.getTime() + offset);
}

/**
 * @title isSameDate
 * @description 时间是否相同, 时间类型支持isDate的类型
 * @param {Time} timeA 比较时间
 * @param {Time} timeB 被比较时间
 * @returns {boolean}
 * @version 2.4.0
 */
function isSameDate(timeA, timeB) {
    if (!isTime(timeA) || !isTime(timeB))
        return false;
    if (timeA.toString() === timeB.toString())
        return true;
    if (new Date(timeA).getTime() === new Date(timeB).getTime())
        return true;
    return false;
}

/**
 * @title intervalDate
 * @description 两个日期间隔天数
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {number}
 * @version 2.4.0
 */
const intervalDate = (startDate, endDate) => Math.ceil(Math.abs(startDate.getTime() - endDate.getTime()) / 86400000);

/**
 * @title dayOfYear
 * @description 指定日期位于今年的第几天
 * @param {Date} date
 * @returns {number}
 */
const dayOfYear = (date = new Date()) => Math.floor((date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / 1000 / 60 / 60 / 24);

/* eslint-disable*/
const s = 1000;
const m = 6_0000;
const h = 3600_000;
const d = 86400_000;
function cron(expression, date = new Date()) {
    const expressions = expression.split(' ');
    const [second, min, hour, day, month, week, year] = expressions;
    const time = {
        year: date.getFullYear(),
        week: date.getDay(),
        month: date.getMonth() + 1,
        day: date.getDate(),
        hour: date.getHours(),
        minute: date.getMinutes(),
        second: date.getSeconds(),
        date,
        gap: 0,
    };
    let gap = 0;
    if (day !== '*') {
        const [start = '0', iGap] = day.split('/');
        // 需要边界校验
        date.setDate(Number(start));
        iGap && (gap += Number(iGap) * d);
        time.day = Number(start);
    }
    if (hour !== '*') {
        const [start = '0', iGap] = hour.split('/');
        date.setHours(Number(start));
        iGap && (gap += Number(iGap) * h);
        time.hour = Number(start);
    }
    if (min !== '*') {
        const [start = '0', iGap] = min.split('/');
        date.setMinutes(Number(start) * m);
        iGap && (gap += Number(iGap) * m);
        time.minute = Number(start);
    }
    if (second !== '*') {
        const [start = '0', iGap] = second.split('/');
        date.setSeconds(Number(start));
        iGap && (gap += Number(iGap) * s);
        time.second = Number(start);
    }
    if (gap === 0) {
        gap = 1000;
    }
    const getDate = () => {
        return new Date(time.year, time.month, time.day, time.hour, time.minute, time.second);
    };
    const newDate = getDate();
    const timestamp = newDate.getTime();
    return {
        next: (count = 1) => {
            return new Date(timestamp + gap * count);
        },
        pre: (count = 1) => {
            return new Date(timestamp - gap * count);
        },
    };
}

function cronToText(cronExpression) {
    const cronParts = cronExpression.split(' ');
    const minute = cronParts[0];
    const hour = cronParts[1];
    const dayOfMonth = cronParts[2];
    const month = cronParts[3];
    const dayOfWeek = cronParts[4];
    let text = '';
    // 解析分钟
    if (minute === '0') {
        text += 'every minute';
    }
    else {
        text += `every ${minute} minutes`;
    }
    // 解析小时
    if (hour !== '0') {
        text += ` past ${hour}`;
    }
    // 解析日期
    if (dayOfMonth !== '*') {
        text += ` on the ${dayOfMonth}th`;
    }
    // 解析月份
    if (month !== '*') {
        text += ` of ${month}`;
    }
    // 解析星期几
    if (dayOfWeek !== '*') {
        text += ` on ${dayOfWeek}`;
    }
    return text;
}
function cronToChinese(cronExpression) {
    const cronParts = cronExpression.split(' ');
    const minute = cronParts[0];
    const hour = cronParts[1];
    const dayOfMonth = cronParts[2];
    const month = cronParts[3];
    const dayOfWeek = cronParts[4];
    let text = '';
    // 解析分钟
    if (minute === '0') {
        text += '每分钟';
    }
    else {
        text += `每隔${minute}分钟`;
    }
    // 解析小时
    if (hour !== '0') {
        text += `，从${hour}点开始`;
    }
    // 解析日期
    if (dayOfMonth !== '*') {
        text += `，每月${dayOfMonth}号`;
    }
    // 解析月份
    if (month !== '*') {
        text += `，在${month}月`;
    }
    // 解析星期几
    if (dayOfWeek !== '*') {
        text += `，星期${dayOfWeek}`;
    }
    return text;
}

/**
 * @title logGroup
 * @description 分组打印(简化console.groupCollapsed)
 * @param {string} name 分组名称
 * @param {unknown[]} ...args 需要分组打印的结果
 * @example logGroup(name[, ...args])
 */
function logGroup(name = '', ...args) {
    console.groupCollapsed(`--- ${name} ---`);
    args.forEach((item) => {
        console.log(item);
    });
    console.groupEnd();
}

/**
 * @title getPathValue
 * @description 通过path 来获取值
 * @param val 待取值
 * @param {string|number} path string 路径 (若路径有`.` 可用`\\.`代替)
 * @returns 通过路径获取对应值
 */
function getPathValue(val, path) {
    if (isEmpty(path)) {
        return val;
    }
    if (likeNumber(path)) {
        if (isArray(val)) {
            return val[Number(path)];
        }
        else if (isString(val) || isObject(val))
            return val[path];
        else {
            return undefined;
        }
    }
    if (!isString(path))
        return undefined;
    const paths = path.split(/(?<!\\)\./);
    let tmpValue = val;
    for (let i = 0; i < paths.length; i++) {
        const item = paths[i].replaceAll('\\.', '.');
        if (isObject(tmpValue)) {
            tmpValue = tmpValue[item];
        }
        else if (isArray(tmpValue) && likeNumber(item)) {
            tmpValue = tmpValue[Number(item)];
        }
        else {
            return undefined;
        }
    }
    return tmpValue;
}
/**
 * @deprecated
 * @replaceWith getPathValue
 */
const toPathValue = getPathValue;

const getConf = (conf) => {
    if (isString(conf) || isNumber(conf))
        return { path: conf, gap: '.' };
    if (isObject(conf)) {
        if (isUndefined(conf.gap))
            conf.gap = '.';
        return conf;
    }
    return { gap: '.' };
};

/**
 * @title toPathObject
 * @param {ObjectType} record
 * @param {PathConf} [conf]
 * @returns {ObjectType}
{
a: {
  b: {
    c: 1
  },
    e: [3, 4]
  }
}
=>
{
  "a.b.c": 1,
  "a.e.0": 3,
  "a.e.1": 4
}
 */
function toPathObject(record, conf) {
    const result = {};
    const { ignoreArray = false, gap = '.' } = getConf(conf);
    const cb = (record, index = '') => {
        if (ignoreArray && isEffectArray(record)) {
            return;
        }
        if (isEffectObject(record) || isEffectArray(record)) {
            for (const key in record)
                cb(record[key], index === '' ? key : index + gap + key);
        }
        else if (index !== '') {
            result[index] = record;
        }
    };
    cb(record, '');
    return result;
}
// const obj = {
//   a: {
//     b: {
//       c: 1,
//     },
//     e: [3, 4],
//   },
// }
// const val = toPathObject(obj, {
//   gap: '$$',
// })
// console.log(val)

/**
 * @title pathObject2Object
 * @description 根据路径获取值
 * @param { ObjectType } record 数据
 * @param { PathConf } [conf]
 * @returns { ObjectType }
{
  "a.b.c": 1,
  "a.e.0": 3,
  "a.e.1": 4
}
=>
{
a: {
  b: {
    c: 1
  },
    e: [3, 4]
  }
}
 */
function pathObject2Object(record, conf) {
    const result = {};
    const { gap = '.' } = getConf(conf);
    const init = (keys, value, record) => {
        if (keys.length === 1)
            record[keys[0]] = value;
        if (keys.length > 1) {
            const [key, ...restKey] = keys;
            if (!record[key])
                record[key] = {};
            init(restKey, value, record[key]);
            if (Object.keys(record[key]).filter((n) => isNumber(Number(n))).length) {
                const tmp = [];
                for (const i in record[key])
                    tmp[i] = record[key][i];
                record[key] = tmp;
            }
        }
    };
    for (const key in record)
        init(key.split(gap), record[key], result);
    return result;
}
// const record = {
//   'a.b.c': 1,
//   'a.e.0': 3,
//   'a.e.1': 4,
// }
// const record2 = {
//   a_$$_b_$$_c: 11,
//   a_$$_e_$$_0: 31,
//   a_$$_e_$$_1: 41,
// }
// console.log(
//   '\n',
//   pathObject2Object(record),
//   '\n',
//   pathObject2Object(record2, { gap: '_$$_' }),
//   '\n',
// )

/**
 * @title setPathKeyValue
 * @description 根据 pathKey 设置 值
 * @param {ObjectType} record
 * @param {PathConf} conf
 * @param {any} value
 */
function setValueByPath(record, conf, value) {
    const { path, gap } = getConf(conf);
    const cb = (obj, keys) => {
        const [key, ...restKeys] = keys;
        try {
            if (!obj[key]) {
                if (isNumber(Number(key))) {
                    obj[key] = [];
                }
                else {
                    obj[key] = {};
                }
            }
            if (restKeys.length) {
                cb(obj[key], restKeys);
                return;
            }
            obj[key] = value;
        }
        catch (error) {
            console.error(error);
        }
    };
    if (isString(path)) {
        cb(record, path.split(gap));
    }
    else if (isNumber(path)) {
        cb(record, [path.toString()]);
    }
}
/**
 * eg:
{
  "a": {
    "b": {
      "c": 5,
      "d": {
        "e": {
          "f": 7
        }
      }
    },
    "e": [
      3,
      4
    ]
  }
}
 */
// const tmp = {
//   a: {
//     b: {
//       c: 1
//     },
//     e: [3, 4]
//   }
// }
// setValueByPath(tmp, 'a.b.c', 5)
// setValueByPath(tmp, 'a.b.d.e.f', 7)
// console.log(tmp)

/**
 * @title equalHelper
 * @description 比较是否值和类型是否相等, 不支持WeakMap, WeakSet
 * @param {unknown} compareValue
 * @param {unknown} beCompareValue
 * @returns {boolean}
 */
function equalHelper(compareValue, beCompareValue) {
    const compareValueType = type(compareValue);
    const beCompareValueType = type(beCompareValue);
    if (compareValue !== beCompareValueType || compareValueType === 'Symbol')
        return false;
    return compareValue === beCompareValue;
}

/**
 * @title equalMap
 * @param {MapType} compareMap
 * @param {MapType} beCompareMap
 * @returns {boolean}
 */
function equalMap(compareMap, beCompareMap, equal = equalHelper) {
    if (!isMap(compareMap) ||
        !isMap(beCompareMap) ||
        compareMap.size !== beCompareMap.size)
        return false;
    for (const [key, value] of compareMap) {
        const beCompareMapTempValue = beCompareMap.get(key);
        if (equal(value, beCompareMapTempValue)) {
            continue;
        }
        return false;
    }
    return true;
}

/**
 * @title equalSet
 * @param {SetType} compareSet
 * @param {SetType} beCompareSet
 * @returns {boolean}
 */
function equalSet(compareSet, beCompareSet, equal = equalHelper) {
    if (!isSet(compareSet) ||
        !isSet(beCompareSet) ||
        compareSet.size !== beCompareSet.size)
        return false;
    const list = [...compareSet];
    const beList = [...beCompareSet];
    for (let i = 0; i < list.length; i++) {
        if (equal(list[i], beList[i])) {
            continue;
        }
        return false;
    }
    // for (const value of compareSet) {
    //   if (beCompareSet.has(value)) continue
    //   return false
    // }
    return true;
}
/**
 * @title equalSet2
 * @param {SetType} compareSet
 * @param {SetType} beCompareSet
 * @returns {boolean}
 */
function equalSet2(compareSet, beCompareSet, equal = equalHelper) {
    if (!isSet(compareSet) ||
        !isSet(beCompareSet) ||
        compareSet.size !== beCompareSet.size)
        return false;
    const list = [...compareSet];
    const beList = [...beCompareSet];
    for (let i = 0; i < list.length; i++) {
        let flag = false;
        for (let j = 0; j < beList.length; j++) {
            if (equal(list[i], beList[j])) {
                flag = true;
                break;
            }
        }
        if (!flag) {
            return false;
        }
    }
    return true;
    // for (let i = 0; i < list.length; i++) {
    //   if (equal(list[i], beList[i])) {
    //     continue
    //   }
    //   return false
    // }
    // for (const value of compareSet) {
    //   if (beCompareSet.has(value)) continue
    //   return false
    // }
}

/**
 * @title equalArray
 * @param {unknown|any[]} compare
 * @param {unknown|any[]} beCompare
 * @returns {boolean}
 */
function equalArray(compare, beCompare, equal = equalHelper) {
    if (isArray(compare) &&
        isArray(beCompare) &&
        compare.length === beCompare.length) {
        for (let i = 0; i < compare.length; i++) {
            const item = compare[i];
            if (equal(item, beCompare[i])) {
                continue;
            }
            return false;
        }
        return true;
    }
    return false;
}
/**
 * @title equalArray2
 * @param {unknown|any[]} compare
 * @param {unknown|any[]} beCompare
 * @returns {boolean}
 */
function equalArray2(compare, beCompare, equal = equalHelper) {
    if (isArray(compare) &&
        isArray(beCompare) &&
        compare.length === beCompare.length) {
        for (let i = 0; i < compare.length; i++) {
            let flag = false;
            for (let j = 0; j < beCompare.length; j++) {
                if (equal(compare[i], beCompare[j])) {
                    flag = true;
                    break;
                }
            }
            if (!flag) {
                return false;
            }
        }
        return true;
    }
    return false;
}

function equalObject(compare, beCompare, equal = equalHelper) {
    if (!isObject(compare) || !isObject(beCompare))
        return false;
    const compareValueKeys = Object.keys(compare);
    const beCompareValueKeys = Object.keys(beCompare);
    if (compareValueKeys.length !== beCompareValueKeys.length)
        return false;
    for (let i = 0; i < compareValueKeys.length; i++) {
        const key = compareValueKeys[i];
        if (equal(compare[key], beCompare[key]))
            continue;
        return false;
    }
    return true;
}

/**
 * @title equal
 * @description 比较是否值和类型是否相等/相同, 不支持类型由`===`来比较
 * @supported 基础数据类型, Object, Array, Map, Set, Date
 * @notSupported WeakMap, WeakSet
 * @param {unknown} compareValue
 * @param {unknown} beCompareValue
 * @returns {boolean}
 * @version 2.4.4
 * @lastUpdate 3.8.0
 */
function equal(compareValue, beCompareValue) {
    const compareValueType = type(compareValue);
    const beCompareValueType = type(beCompareValue);
    if (compareValueType !== beCompareValueType)
        return false;
    if (compareValueType === 'Object')
        return equalObject(compareValue, beCompareValue, equal);
    if (compareValueType === 'Array')
        return equalArray(compareValue, beCompareValue, equal);
    if (compareValueType === 'Map')
        return equalMap(compareValue, beCompareValue, equal);
    if (compareValueType === 'Set')
        return equalSet(compareValue, beCompareValue, equal);
    if (Number.isNaN(compareValue))
        return Number.isNaN(beCompareValue);
    if (isDate(compareValue) && isDate(beCompareValue))
        return compareValue.getTime() === beCompareValue.getTime();
    return compareValue === beCompareValue;
}

/**
 * @title like
 * @description 比较是否值和类型是否相等/相同, 不支持类型由`===`来比较 或 转字符串后比对
 * @supported 基础数据类型, Object, Array, Map, Set, Date
 * @notSupported WeakMap, WeakSet
 * @param {unknown} compareValue
 * @param {unknown} beCompareValue
 * @returns {boolean}
 * @version 3.8.0
 */
function like(compareValue, beCompareValue) {
    if (compareValue === beCompareValue) {
        return true;
    }
    if ((isEmpty(compareValue) || compareValue === '') &&
        (isEmpty(beCompareValue) || beCompareValue === '')) {
        return true;
    }
    const compareValueType = type(compareValue);
    // const beCompareValueType = type(beCompareValue)
    if (compareValueType === 'Object')
        return equalObject(compareValue, beCompareValue, like);
    if (compareValueType === 'Array')
        return equalArray2(compareValue, beCompareValue, like);
    if (compareValueType === 'Set')
        return equalSet2(compareValue, beCompareValue, like);
    if (compareValueType === 'Map')
        return equalMap(compareValue, beCompareValue, like);
    if (Number.isNaN(compareValue))
        return Number.isNaN(beCompareValue);
    if (isDate(compareValue) && isDate(beCompareValue))
        return compareValue.getTime() === beCompareValue.getTime();
    const compareValueStr = stringify(compareValue).replace(/'|"|\s/gi, '');
    const beCompareValueStr = stringify(beCompareValue).replace(/'|"|\s/gi, '');
    return compareValueStr === beCompareValueStr;
}

/**
 * @title compareNumber
 * @param {number} val 被比较值
 * @param {number|string} valer 比较值 / 可为正则 / [number, number] / 字符串(=number,<=number...)
 * @returns {boolean}
 * @lastUpdate @3.2.0
 */
function compareNumber(val, valer) {
    const valType = type(val);
    const valerType = type(valer);
    if (valerType === 'RegExp') {
        return valer.test(String(val));
    }
    if (valType !== 'Number') {
        return false;
    }
    if (val === valer) {
        return true;
    }
    if (isArray(valer) && valer.length > 0) {
        let min = -Infinity;
        let max = Infinity;
        if (!isEmpty(valer[0])) {
            min = Number(valer[0]);
        }
        if (valer.length > 1 && !isEmpty(valer[1])) {
            max = Number(valer[1]);
        }
        return val > min && val < max;
    }
    if (valerType === 'String') {
        const [matNum, Sym = '='] = /(?<=([<>=!]+))[0-9]+/gi.exec(valer) || [];
        switch (Sym) {
            case '=': return val === Number(matNum);
            case '>': return val > Number(matNum);
            case '>=': return val >= Number(matNum);
            case '<': return val < Number(matNum);
            case '<=': return val <= Number(matNum);
            case '<>':
            case '!=': return val != Number(matNum);
        }
    }
    return false;
}
/**
 * @title compareValue
 * @param {Val} val 被比较值
 * @param {Valer} valer 比较值 / 可为正则
 * @param {string} [path] 值的路径 用逗号隔开
 * @returns {boolean}
 */
function compareValue(val, valer, path) {
    if (path) {
        val = toPathValue(val, path);
    }
    if (equal(val, valer))
        return true;
    if (isRegExp(valer))
        return valer.test(String(val));
    if (isNumber(val))
        return compareNumber(val, valer);
    if (isNumber(Number(val)))
        return compareNumber(Number(val), valer);
    return false;
}

function deepClonePredicate(value) {
    switch (type(value)) {
        case 'RegExp':
            return new RegExp(value);
        case 'Array':
        case 'Object': {
            const cloneObj = new value.constructor();
            for (const key in value) {
                if (value.hasOwnProperty(key)) {
                    cloneObj[key] = deepClonePredicate(value[key]); // 递归拷贝  
                }
            }
            return cloneObj;
        }
        case 'Date': {
            return new Date(value);
        }
    }
    return value;
}
/**
 * @title deepClone
 * @description 深拷贝
 * @param {unknown} value
 * @returns {Result}
 * @version 2.4.1
 */
function deepClone(value) {
    return deepClonePredicate(value);
}

/**
 * @title toFirstUpperCase
 * @description 首字母大写
 */
const toFirstUpperCase = (value) => value.replace(/\b.*/g, (word) => word.substring(0, 1).toUpperCase() + word.substring(1));
/**
 * @title toFirstLowerCase
 * @description 首字母小写
 */
const toFirstLowerCase = (value) => value.replace(/\b.*/g, (word) => word.substring(0, 1).toLowerCase() + word.substring(1));
/**
 * @title toUpperCase
 * @description 全部大写
 */
const toUpperCase = (value) => value.toUpperCase();
/**
 * @title toLowerCase
 * @description 全部小写
 */
const toLowerCase = (value) => value.toLowerCase();

/**
 * @title guard
 * @param {Function} func
 * @param {unknown} errorReturnValue
 * @returns {(...args:unknown[])=>unknown}
 */
function guard(func, errorReturnValue) {
    return (...args) => {
        try {
            if (isFunction(func))
                return func(...args);
            return errorReturnValue;
        }
        catch (error) {
            return errorReturnValue;
        }
    };
}
/**
 * @title asyncGuard
 * @param {Function} func
 * @param {unknown} errorReturnValue
 * @returns {(...args:unknown[])=>unknown}
 */
function asyncGuard(func, errorReturnValue) {
    return async (...args) => {
        try {
            if (isAsyncFunction(func))
                return await func(...args);
            if (isFunction(func))
                return func(...args);
            return errorReturnValue;
        }
        catch (error) {
            return errorReturnValue;
        }
    };
}

/**
 * @title has
 * @description beComparedValue 包含 comparedValue 的值, 模糊匹配, 忽略换行和空格
 * @param {number|string|ObjectType} beComparedValue 被比较的值(大)
 * @param {number|string|ObjectType} compareValue 比较的值(小)
 * @example has('1223', '123') => true
 * @returns {boolean}
 */
function has(beComparedValue, compareValue) {
    if (compareValue === beComparedValue &&
        type(compareValue) === type(beComparedValue)) {
        return true;
    }
    const newBeComparedValue = stringify(beComparedValue).replace(/'|"|\s/gi, '');
    const newCompareValue = stringify(compareValue).replace(/'|"|\s/gi, '');
    if (newBeComparedValue.includes(newCompareValue)) {
        return true;
    }
    const compareValues = newCompareValue.split('');
    let newBeValue = newBeComparedValue;
    for (let i = 0; i < compareValues.length; i++) {
        const item = compareValues[i];
        if (!newBeValue.includes(item)) {
            return false;
        }
        newBeValue = newBeValue.replace(item, '');
    }
    return newBeValue.length >= 0;
}

/**
 * @title  sleep
 * @description 睡眠 / 异步延迟
 * @param {number} [ms=500]
 * @returns {Promise<void>}
 */
function sleep(ms = 500) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}
// // 使用示例
// async function doSomething() {
//   console.log('Start');
//   await sleep(2000);
//   console.log('End after 2 seconds');
// }
// doSomething();

/**
 * @title filter<T>
 * @description 单层过滤
 * @param {T[]} list 待过滤数组
 * @param {FilterCondition<T>} filterCondition 过滤条件
 * @param {boolean} [retainNotObject=false] 是否保留非对象项
 * @returns {T[]}
 * @version 2.3.1
 */
function filter(list, filterCondition, retainNotObject = false) {
    if (!isArray(list))
        return [];
    if (!filterCondition || !list || list.length === 0)
        return list;
    if (isFunction(filterCondition)) {
        return list.filter(filterCondition);
    }
    if (isEffectObject(filterCondition))
        return list.filter((item) => {
            if (!isObject(item))
                return retainNotObject;
            for (const key in filterCondition) {
                const unit = filterCondition[key];
                const originValue = item[key];
                if (originValue === unit)
                    break;
                if (isRegExp(unit)) {
                    const val = isString(originValue) ? originValue : stringify(originValue);
                    if (!unit.test(val))
                        return false;
                    break;
                }
                return false;
            }
            return true;
        });
    return list;
}

/**
 * @title isFloat
 * @description 判断数是否为浮点型
 * @param num 待检测的数据类型
 * @returns boolean
 */
function isFloat(num) {
    return (num % 1) !== 0;
}
/**
 * @title toFloat
 * @description 转换为指定位数的浮点数
 * @param num { number } 数字
 * @param fixed { number } 小数点位数
 * @returns { number }
 */
// eslint-disable-next-line @typescript-eslint/no-inferrable-types
function toFloat(num, fixed = 1) {
    return Number(num.toFixed(fixed));
}

/**
 * @title random
 * @description 随机数
 * @param lower { number } 下限
 * @param upper { number } 上限
 * @param floating { number = 0 } 是否返回浮点数(位数), 0: 整数
 */
function random(lower = 0, upper = 1, floating = 0) {
    if (lower === 0 && upper === 1) {
        return Math.random();
    }
    if (isFloat(upper) || isFloat(lower)) {
        const len = upper > lower ? upper : lower;
        if (floating === 0) {
            floating = len;
        }
    }
    const result = lower + Math.random() * (upper - lower);
    if (floating)
        return toFloat(result, floating);
    return Math.ceil(result);
}

/**
 * @title selects<T>
 * @description 指定范围 来随机选择数组元素
 * @param {T[]} list
 * @param {number} [min=0] 最小值(包括)
 * @param {number} [max=list.length-1] 最大值(包括)
 * @returns {T[]}
 */
function selects(list, min = 0, max = list.length - 1) {
    if (max > list.length)
        max = list.length - 1;
    let len = random(min, max);
    const result = [];
    let index = 0;
    while (len--) {
        index = ~~(Math.random() * list.length);
        result.push(list[index]);
        list.splice(index, 1);
    }
    return result;
}
/**
 * @title select<T>
 * @description 选择数组其中一项, 不指定就随机选一
 * @param {T[]} list 待选择数组
 * @param {?number} index  指定选择索引(可为负数)
 * @returns {T|null} 选择项
 */
function select(list = [], index) {
    if (isArray(list)) {
        const len = list.length;
        if (len === 0)
            return null;
        if (!index && index !== 0)
            return list[~~(Math.random() * list.length)];
        if (index > len)
            return list[index % len];
        if (index > -1)
            return list[index];
        if (index < 0)
            return list[list.length + (index % len)];
    }
    return null;
}

/**
 * @title toArray<T>
 * @description 将非数组转换为数组, 对象会转换成 Array([key,value])
 * @param {T|T[]} value
 * @returns {T[]}
 * @lastUpdate 3.3.0
 */
function toArray(value) {
    if (Array.isArray(value))
        return value;
    if (isEmpty(value))
        return [];
    if (isSet(value))
        return [...value];
    if (isMap(value)) {
        return Array.from(value);
    }
    if (isObject(value)) {
        return Object.keys(value).map(key => {
            return [key, value[key]];
        });
    }
    return [value];
}

/**
 * @title chunk<T>
 * @description 通过 size 切割数组
 * @param {T[]} list
 * @param {number} size 切割点索引
 * @returns {T[][]} [ [切割点前数据], [切割点后数据] ]
 */
function chunk(list, size) {
    return [list.slice(0, size), list.slice(size)];
}

/**
 * @title zip<T>
 * @description: 每个数组同样的下标组成一个新数组
 * @param {...T[]} ...arrays
 * @returns {T[][]}
 * @version 2.1.11
 * @eg ```ts
 zip(['fred', 'barney'], [30, 40], [true, false])
 => [['fred', 30, true], ['barney', 40, false]]
 ```
 */
function zip(...arrays) {
    const result = [];
    const length = arrays.length;
    let index = 0;
    while (index < length - 1) {
        const item = arrays.map(item => {
            return item[index];
        });
        result.push(item);
        index++;
    }
    return result;
}
/**
 * @title zipObject<Value>
 * @description: 第一个数组是对象的key, 第二个数组是对象的value
 * @param {(string|number)[]} keys
 * @param {Value[]} values
 * @returns {ObjectType<Value>}
 * @version 2.1.11
 * @eg ```ts
 zipObject(['a', 'b'], [1, 2])
 => { 'a': 1, 'b': 2 }
 ```
 */
function zipObject(keys, values) {
    const record = {};
    keys.forEach((key, index) => {
        record[key] = values[index];
    });
    return record;
}

/**
 * @title flat<T>
 * @description 数组扁平化
 * @param {T[]} list
 * @param {number} [depth=1] 深度
 * @returns {T[]}
 * @version: 2.2.0
 * @lastUpdate 3.4.0
 */
function flat(list, depth = 1) {
    if (!Array.isArray(list))
        return toArray(list);
    if (list.length === 0 || depth < 1)
        return [];
    if (depth === 1)
        return list.flatMap(i => i);
    return list.flat(depth);
}

/**
 * @title drop<T>
 * @description 去除前n个元素(影响原数据)
 * @param {T[]} list 数组
 * @param {number} n=0 要去除元素个数
 * @returns {T[]} 剩余切片
 */
function drop(list = [], n = 0) {
    if (!isArray(list))
        return toArray(list);
    while (n--) {
        if (list.length < 1)
            return [];
        if (list.length > 0)
            list.shift();
    }
    return list;
}
/**
 * @title dropRight<T>
 * @description 从右往左删除的指定个数
 * @param {T[]} list 要处理的数组
 * @param {number} [n=1] 需要删除的元素数量
 * @returns {T[]}
 */
function dropRight(list, n = 1) {
    return list.splice(0, list.length - n);
}

/**
 * @title unique<T>
 * @description 去除数组重复项
 * @param {T[]} list 待过滤数组
 * @returns {T[]}
 */
function unique(list) {
    return [...new Set(list)];
}

/**
 * @title fill<T>
 * @description 在原有数组上改变, 修改指定位置的值
 * @param {T[]} array 待填充改变的数组
 * @param {T|(index:number)=>T} value 填充值
 * @param {number} [num=0] 填充个数
 * @returns
 */
function fill(array, value, num = 0) {
    while (num--) {
        array.push(isFunction(value)
            ? value(array.length)
            : value);
    }
    return array;
}

function getSortNum(value, sortIndex) {
    if (isNumber(value))
        return value;
    if (isString(value)) {
        const num = Number(value);
        return isNumber(num) ? num : value;
    }
    if (isEmpty(sortIndex))
        return 0;
    if (isObject(value)) {
        const newValue = toPathValue(value, sortIndex);
        if (isString(newValue)) {
            const num = Number(newValue);
            return isNumber(num) ? num : newValue;
        }
        if (isNumber(newValue)) {
            return newValue;
        }
    }
    return 0;
}
/**
 * @title descSort<T=object>
 * @description 降序排序(从大到小排序)
 * @param {T[]} list
 * @param {string|number} [sortIndex] 索引路径, 参考`toPathValue`的路径
 * @returns {T[]}
 * @create 3.3.0
 * @lastUpdate 3.3.0
 */
function descSort(list, sortIndex) {
    const handler = (before, after) => {
        const newAfter = getSortNum(after, sortIndex);
        const newBefore = getSortNum(before, sortIndex);
        if (isString(newAfter) && isString(newBefore))
            return compareString(newAfter, newBefore) ? 1 : -1;
        if (isNumber(newAfter) && isNumber(newBefore))
            return newAfter - newBefore;
        return 0;
    };
    if (isEffectArray(list)) {
        return list.sort(handler);
    }
    return [];
}
/**
 * @title ascSort<T=object>
 * @description 升序排序(从小到大排序)
 * @param {T[]} list
 * @param {string|number} sortIndex 索引路径, 参考toPathValue的路径
 * @returns {T[]}
 * @create 3.3.0
 * @lastUpdate 3.3.0
 */
function ascSort(list, sortIndex) {
    const handler = (before, after) => {
        const newAfter = getSortNum(after, sortIndex);
        const newBefore = getSortNum(before, sortIndex);
        if (isString(newAfter) && isString(newBefore))
            return compareString(newAfter, newBefore) ? -1 : 1;
        if (isNumber(newAfter) && isNumber(newBefore))
            return newBefore - newAfter;
        return 0;
    };
    if (isEffectArray(list)) {
        return list.sort(handler);
    }
    return [];
}

/**
 * @title pageQuery
 * @description 对数组进行分页查询以及管理
 * @param {PageQueryProps} props
 * @returns
 */
function pageQuery(originDataSource = [], props = {}) {
    const { uniqueIndex = 'id', noRangeFields, keepFields, handleValue, fields, } = props;
    // 原始数据
    let dataSource = [...originDataSource];
    const getValue = (record, key) => {
        let value = record[key];
        if (fields && isFunction(fields[key])) {
            return fields[key](value, key);
        }
        else if (handleValue) {
            return handleValue(value, key);
        }
        if (isEmpty(value))
            return '';
        if (isEffectArray(keepFields) && keepFields.includes(key)) {
            return value;
        }
        if (isString(value)) {
            return value.trim().toUpperCase();
        }
        if (isNumber(value)) {
            return stringify(value);
        }
        return value;
    };
    const isEqual = (item, params, key) => {
        const value = getValue(item, key);
        const beValue = getValue(params, key);
        if (!(isEffectArray(noRangeFields) &&
            noRangeFields.includes(key)) &&
            likeNumber(value) &&
            isEffectArray(beValue) &&
            beValue.length === 2) {
            const [min, max] = beValue;
            if (likeNumber(min) && likeNumber(max)) {
                return Number(value) >= Number(min) &&
                    Number(value) <= Number(max);
            }
            return false;
        }
        if (isString(value) && isString(beValue)) {
            return value.indexOf(beValue) > -1;
        }
        return equal(value, beValue);
    };
    const getDataSource = (params, config) => {
        let newDataSource = [...dataSource];
        if (isEffectObject(config)) {
            const { sortBy } = config;
            if (isEffectObject(sortBy)) {
                for (const key in sortBy) {
                    const sortIndex = sortBy[key];
                    if (sortIndex === 'desc') {
                        newDataSource = descSort(newDataSource, key);
                    }
                    if (sortIndex === 'asc') {
                        newDataSource = ascSort(newDataSource, key);
                    }
                }
            }
        }
        if (isEffectObject(params)) {
            newDataSource = newDataSource.filter(item => {
                for (let key in params) {
                    if (!isEqual(item, params, key))
                        return false;
                }
                return true;
            });
        }
        return newDataSource;
    };
    /**
     * @title getPage
     * @description 获取分页数据
     * 1,params[key]. 若值为[number, number], 且被查询值为数字或数字字符串, [number, number]当做范围, 若不想这样子指定, 需要在 pageQuery 指定
     * @param {Object} params 查询条件
     * @param {Pagination} pagination 分页信息
     * @returns {dataSource: DataSource, pagination: Pagination}
     */
    const getPage = (params, pagination = {}) => {
        const { current = 1, pageSize = 10, sortBy } = pagination;
        const newDataSource = getDataSource(params, { sortBy });
        return {
            dataSource: newDataSource.slice((current - 1) * pageSize, current * pageSize),
            pagination: {
                current,
                pageSize,
                total: newDataSource.length
            }
        };
    };
    const del = (indexes) => {
        if (isEffectArray(indexes)) {
            dataSource = getDataSource()
                .filter(item => !indexes.includes(item[uniqueIndex]));
            return;
        }
        if (isString(indexes)) {
            dataSource = getDataSource()
                .filter(item => item[uniqueIndex] !== indexes);
            return;
        }
    };
    const add = (record) => {
        if (isObject(record)) {
            if (isEmpty(record[uniqueIndex])) {
                record[uniqueIndex] = '__vid__' + vid();
            }
            dataSource.unshift(record);
            return;
        }
        if (isEffectArray(record)) {
            record.forEach(add);
            return;
        }
    };
    return {
        dataSource,
        getPage,
        getDataSource,
        del,
        add,
    };
}

/**
 * @title existKeys
 * @description 判断对象是否拥有指定keys
 * @param obj object
 * @param keys string[] | string
 * @returns boolean
 */
function existKeys(obj, keys) {
    const objKeys = Object.keys(obj);
    keys = toArray(keys);
    for (let i = 0; i < keys.length; i++) {
        if (objKeys.includes(keys[i]))
            return true;
    }
    return false;
}

/**
 * @title omit<T extends ObjectType>
 * @description 去除集合属性
 * @param {T|Map} collection
 * @param {CollectionKey[]} propertys
 * @returns {T|Map}
 */
function omit(collection, propertys = []) {
    if (isMap(collection)) {
        const newMap = new Map(collection);
        propertys.forEach(property => {
            newMap.delete(property);
        });
        return newMap;
    }
    if (isObject(collection)) {
        const newRecord = { ...collection };
        const keys = Object.keys(collection);
        propertys.forEach(property => {
            isString(property)
                && keys.includes(property)
                && delete newRecord[property];
        });
        return newRecord;
    }
    return {};
}

/**
 * @title serialize
 * @description 序列化对象
 * @param query object
 * @param encode boolean = false
 * @returns string
 */
function serialize(query, encode = false) {
    if (isObject(query))
        return Object.keys(query)
            .map((key) => {
            const value = query[key];
            if (isString(value)
                || isNumber(value)
                || isBoolean(value))
                return `${key}=${encode
                    ? encodeURIComponent(value)
                    : value}`;
        })
            .join('&');
    return '';
}

function ObjectEntity(target = {}, config) {
    const { defaultValue, defaultKey } = config || {};
    const keys = Object.keys(target);
    const values = Object.values(target);
    const get = (key) => keys.includes(key) ? target[key] : defaultValue;
    const getKey = (value) => {
        if (values.includes(value)) {
            for (let i = 0; i < keys.length; i++)
                if (equal(target[keys[i]], value))
                    return keys[i];
        }
        return defaultKey;
    };
    const list = (keyName = 'key', valueName = 'value', keyValueReverse = false) => {
        return Object.entries(target)
            .map(([key, value]) => {
            if (keyValueReverse) {
                return {
                    [valueName]: key,
                    [keyName]: value,
                };
            }
            return {
                [keyName]: key,
                [valueName]: value
            };
        });
    };
    return {
        target,
        keys,
        values,
        get,
        getKey,
        list,
    };
}

/**
 * @title delay<Params,Result>
 * @param func 指定函数
 * @param {number} [delayTime=0] 延迟时间
 * @param {Params} ...args 传输参数
 * @returns {Promise<Result>} func执行结果(Promise)
 */
function delay(func, delayTime = 0, ...args) {
    return new Promise((resolve) => {
        setTimeout(() => {
            if (isFunction(func)) {
                resolve(func(...args));
            }
            else {
                resolve(undefined);
            }
        }, delayTime);
    });
}

/**
 * @title getLength
 * @description 获取集合长度(大小)
 * @param {Collection} collection
 * @returns {number}
 */
function getLength(collection) {
    if (isArray(collection)
        || isString(collection))
        return collection.length;
    if (isObject(collection))
        return Object.keys(collection).length;
    if (isSet(collection)
        || isMap(collection))
        return collection.size;
    return 0;
}
/**
 * @title getIndex
 * @description 获取集合key
 * @param {Collection} collection
 * @param {CollectionKey} key
 * @returns {CollectionKey|undefined}
 */
function getIndex(collection, key) {
    if (isArray(collection)
        || isString(collection)
        || isSet(collection)) {
        const len = getLength(collection);
        let newIndex = isNumber(key) ? key : Number(key);
        if (isEmpty(newIndex) || !isNumber(newIndex))
            return undefined;
        if (len === 0)
            return 0;
        if (newIndex > len)
            return len - 1;
        if (newIndex < 0)
            return len + newIndex;
        return newIndex;
    }
    if (isMap(collection) &&
        collection.has(key))
        return key;
    if (isEffectObject(collection)) {
        const newKey = isString(key) ? key : stringify(key);
        if (Object.keys(collection).includes(newKey))
            return newKey;
    }
    return undefined;
}

/**
 * @title at
 * @description 通过下标获取值
 * @param {Collection} collection
 * @param {CollectionKey} [index=0] 可为负数
 * @returns {CollectionValue}
 * @version 2.6.0
 */
function at(collection, index = 0) {
    if (isEmpty(collection))
        return undefined;
    const len = getLength(collection);
    if (len === 0)
        return undefined;
    const newIndex = getIndex(collection, index);
    if (isEmpty(newIndex))
        return undefined;
    if (isNumber(newIndex) &&
        (isString(collection) || isArray(collection)))
        return collection[newIndex];
    if (isObject(collection) &&
        (isNumber(newIndex) || isString(newIndex)))
        return collection[newIndex];
    if (isMap(collection))
        return collection.get(newIndex);
    let i = -1;
    if (isSet(collection))
        for (const value of collection)
            if (++i === newIndex)
                return value;
}

/**
 * @title nth
 * @description 通过指定规则回去集合参数
 * @param {Collection} collection
 * @param {string|number} [index] : 可以输入下标/2n+1 这种格式
 * @return {Collection|CollectionValue}
 */
function nth(collection, index) {
    if (isEmpty(index) || isEmpty(collection) || index === 'n') {
        return collection;
    }
    if (isString(index) &&
        index.includes('n') &&
        /\d?n[+-]?\d?/.exec(index) &&
        (isArray(collection) || isString(collection))) {
        const params = /([-+]?\d)?n([+-]?\d)?/.exec(index);
        if (isArray(params) && params.length === 3) {
            const mult = Number(params[1]);
            const gap = Number(params[2]);
            const getNewIndex = (num) => {
                if (isNumber(mult)) {
                    num = num * mult;
                }
                if (isNumber(gap)) {
                    num = num + gap;
                }
                return num;
            };
            let i = 0;
            let j = 0;
            const len = collection.length;
            const result = [];
            while (j < len && i < len) {
                j = getNewIndex(i) + 1;
                if (j >= len)
                    break;
                result.push(at(collection, j));
                i++;
            }
            return result;
        }
    }
    return at(collection, index);
}

/**
 * @title includes
 * @description 检查 value(值) 是否在 collection(集合); 如果指定 fromIndex 是负数，那么从 collection(集合) 的结尾开始检索
 * @param {Array|Object|string} collection 要检索的集合
 * @param {number|string} value 要检索的值
 * @param {number|string} [fromIndex=0] 要检索的索引
 * @returns {boolean}
 */
function includes(collection, value, fromIndex = 0) {
    const newIndex = getIndex(collection, fromIndex);
    if (isString(collection) && isNumber(newIndex)) {
        const newCollection = collection.substring(newIndex);
        const newValue = isString(value) ? value : stringify(value);
        return newCollection.indexOf(newValue) > -1;
    }
    if (isArray(collection)) {
        if (fromIndex === 0)
            return collection.includes(value);
        if (isNumber(newIndex)) {
            for (let i = newIndex; i < collection.length; i++)
                if (equal(collection[i], value))
                    return true;
            return false;
        }
    }
    if (isObject(collection)) {
        if (fromIndex === 0)
            return Object.values(collection).includes(value);
        if (isString(newIndex)) {
            return equal(collection[newIndex], value);
        }
    }
    return false;
}

/**
 * @title concat<T>
 * @description 连接多个数组
 * @param {...unknown[]} ...list 多个数组
 * @returns {T[]}
 * @lastUpdate 2.6.0
 */
function concat(...list) {
    if (!list || list.length === 0)
        return [];
    const result = [];
    const len = list.length;
    let i = -1;
    while (++i < len) {
        const item = list[i];
        if (isArray(item)) {
            item.forEach(unit => result.push(unit));
            continue;
        }
        if (isSet(item)) {
            item.forEach(unit => result.push(unit));
            continue;
        }
        result.push(item);
    }
    return result;
}

/**
 * @title copyWithin<T>
 * @description 浅复制数组或子串的一部分到同一数组中的另一个位置，并返回它，不会改变原数组/字串的长度
 * @param {string|T[]} value 代复制子串/数组
 * @param {number} target 复制结果目标下标
 * @param {number} [start=0] 被复制起点
 * @param {number} end 被复制终点, 不指定及复制最后
 * @returns {string|T[]}
 * @version 2.6.0
 */
function copyWithin(value, target, start = 0, end) {
    if (value.length === 0)
        return [];
    if (isString(value)) {
        return value.split('').copyWithin(target, start, end).join('');
    }
    return [...value].copyWithin(target, start, end);
}

/**
 * @title entries
 * @description 返回一个数组迭代对象
 * @param {string|T[]} value
 * @returns
 */
function entries(value) {
    if (isString(value)) {
        return value.split('').entries();
    }
    return value.entries();
}

/**
 * @title indexOf
 * @description 拓展原生 Array.prototype.indexOf | String.prototype.indexOf 的适用方法, 拓展 Map, Set
 * @param {Collection} collection
 * @param {CollectionValue} value
 * @returns {CollectionKey}
 */
function indexOf(collection, value) {
    if (isString(collection))
        return collection.indexOf(value);
    if (isArray(collection)) {
        return collection.indexOf(value);
    }
    if (isObject(collection)) {
        const keys = Object.keys(collection);
        if (keys.length === 0)
            return undefined;
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            if (equal(collection[key], value))
                return key;
        }
        return undefined;
    }
    if (isMap(collection)) {
        const mapCollection = collection;
        if (mapCollection.size === 0)
            return undefined;
        for (const [key, mapValue] of mapCollection)
            if (equal(mapValue, value))
                return key;
        return undefined;
    }
    if (isSet(collection)) {
        const setCollection = collection;
        let index = -1;
        if (setCollection.size === 0)
            return -1;
        for (const setValue of setCollection) {
            ++index;
            if (equal(setValue, value))
                return index;
        }
        return -1;
    }
    return undefined;
}

/**
 * @title pipe<Params,Result>
 * @description 管道函数, 链接前函数的值为下一个函数的参
 * @param {Function[]} ...funcs 函数数组
 * @returns {(...args: Params)=>Result}
 */
function pipe(...funcs) {
    return function (...args) {
        let params = args;
        for (let i = 0; i < funcs.length; i++) {
            const item = funcs[i];
            if (isFunction(item)) {
                params = toArray(item(...params));
                continue;
            }
        }
        return at(params);
    };
}

/**
 * @title memoize<Params,Result>
 * @description 缓存方法结果, 若参数相同, 会返回相同结果
 * @param {Function} func
 * @returns {(...args: Params)=>Result}
 */
function memoize(func) {
    const cache = new Map();
    return function (...args) {
        if (!isFunction(func)) {
            return undefined;
        }
        const key = stringify(args);
        if (cache.has(key)) {
            return cache.get(key);
        }
        const result = func.apply(this, args);
        cache.set(key, result);
        return result;
    };
}

/**
 * @title once<Params,Result>
 * @description  fn 方法只会执行一次, 多次执行返回值为第一次的返回值
 * @param {Function} func 指定值运行一次的方法
 * @returns {Function} 返回封装后的方法
 */
function once(func) {
    let returnValue = undefined;
    let canRun = true;
    return function (...args) {
        if (canRun) {
            returnValue = func.apply(this, args);
            canRun = false;
        }
        return returnValue;
    };
}

/**
 * @title runFunc
 * @description 运行函数, 支持普通函数和async函数, 否则返回func
 * @param func
 * @param ...args
 * @returns
 */
function runFunc(func, ...args) {
    if (type(func) === 'Function' || type(func) === 'AsyncFunction') {
        return func(...args);
    }
}

function loopCore(target, callback, index = 0, values = [], indexes = []) {
    if (index < target.length) {
        const nowTarget = target[index];
        if (isArray(nowTarget)) {
            for (let i = 0; i < nowTarget.length; i++) {
                const newValues = values.concat(nowTarget[i]);
                const newIndexes = indexes.concat(i);
                loopCore(target, callback, index + 1, newValues, newIndexes);
            }
        }
        else {
            const newValues = values.concat(nowTarget);
            const newIndexes = indexes.concat(0);
            return loopCore(target, callback, index + 1, newValues, newIndexes);
        }
    }
    else {
        callback(values, indexes);
    }
}
/**
 * @title loop<T>
 * @description: 指定数组遍历遍历
 * @param {T[][]} target
 * @param {(values?:T[],indexes?:number[])=>void} callback
 * @update 3.5.0
 */
function loop(target, callback) {
    if (isEmpty(callback))
        return;
    loopCore(target, callback);
    return;
}

/**
 * @title throttle<Params>
 * @description 节流: 用于限制函数触发频率, 每个delay时间间隔，最多只能执行函数一次
 * @param {Function} fn 待处理函数
 * @param {number} interval number 间隔
 */
function throttle(fn, interval) {
    let lastTime = 0;
    return function () {
        const timeSinceLastExecution = Date.now() - lastTime;
        if (!lastTime || timeSinceLastExecution >= interval) {
            fn.apply(this, arguments);
            lastTime = Date.now();
        }
    };
}

/**
 * @title debounce<Params>
 * @description
 * -- 防抖:  时间内只会执行一次 可以减少函数触发的频率
 * -- 当函数触发时，使用一个定时器延迟执行操作。
 * -- 当函数被再次触发时，清除已设置的定时器，重新设置定时器。
 * -- 如果上一次的延迟操作还未执行，则会被清除。
 * @param {Function} fn
 * @param {number} interval
 * @returns {Func<Params>}
 */
function debounce(fn, interval) {
    let timer = 0;
    return () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            fn.apply(this, arguments);
        }, interval);
    };
}

/**
 * @title curry<Params,Result>
 * @description 柯里化
 * @param {Function} func 待柯里化函数
 * @param {number} [len=1] 待柯里化参数个数
 * @returns 柯里化函数
 */
function curry(func, len = 1) {
    if (!isFunction(func)) {
        return undefined;
    }
    // 第一次执行时，定义一个数组专门用来存储所有的参数
    let _args = [];
    const _resFn = function (...args) {
        _args = _args.concat(args);
        if (_args.length < len) {
            return _resFn;
        }
        return func(..._args);
    };
    return _resFn;
}

/**
 * @title bind<Params,Result>
 * @description thisArg绑定func的this，并且func会接收partials附加参数
 * @param {Function} func 绑定的函数
 * @param {Params} partials 附加的部分参数
 * @returns 新的绑定函数
 */
function bind(func, ...partials) {
    return function (...args) {
        if (func)
            return func.call(this, ...[...partials, ...args]);
        return undefined;
    };
}

/**
 * @title EventEmitter<T, U>
 * @description 简易观察者模式
 * @property $cache {Record<string,T[]>} 事件缓存
 * @property $on {(name:string,fn:T)=>void} 绑定事件
 * @property $off {(name: string)=>boolean} 移除事件的名称
 * @property $once {<Params extends any[] = any[]>(name: string, ...args: Params)=>U[]} 触发后就移除当前事件
 * @property $emit {<Params extends any[] = any[]>(name: string, ...args: Params)=> U[]} 触发事件
 * @eg ```js
 const ev = new EventEmitter()
 ev.$on('fn1', ()=>console.log(1))
 ev.$on('fn2', ()=>console.log(2))
 ev.$emit('fn1')
 // 1
 ev.$emit('fn2')
 // 2
 ```
 */
class EventEmitter {
    /**
    * @description 事件缓存
    */
    $cache = {};
    /**
      * @description 绑定事件
      * @param name 事件name
      * @param fn 待触发事件
      */
    $on(name, fn) {
        if (this.$cache[name]) {
            if (Array.isArray(this.$cache[name])) {
                this.$cache[name].push(fn);
            }
            else {
                this.$cache[name] = [fn];
            }
            return;
        }
        this.$cache[name] = [fn];
    }
    /**
      * @param name 移除事件的名称
      */
    $off(name) {
        if (!this.$cache[name])
            return false;
        delete this.$cache[name];
        return true;
    }
    /**
       * @description 触发后就移除当前事件
       * @param name 待触发事件name
       * @param args 触发事件的参数
       */
    $once(name, ...args) {
        if (this.$cache && this.$cache[name]) {
            const result = this.$cache[name].map((i) => runFunc(i, ...args));
            delete this.$cache[name];
            return result;
        }
        return [];
    }
    /**
     * @param name 待触发事件name
     * @param args 触发事件的参数
     */
    $emit(name, ...args) {
        // 创建副本，如果回调函数内继续注册相同事件，会造成死循环
        if (!this.$cache[name])
            return [];
        return this.$cache[name].map((i) => runFunc(i, ...args));
    }
}

/**
 * @title ProxyObject<T extends object>
 * @description 代理object的属性, 结合 Proxy 和 Object 用法
 * @param object {T} 代理的object
 * @param props {?ProxyObjectProp[]} object 属性配置
 * @param handler {?ProxyHandler<T>}
 * @returns {Proxy<T>}
 */
function ProxyObject(object, props, handler = {}) {
    if (isEffectArray(props) && isObject(object)) {
        const propsConfig = {};
        const supplyConfigs = {};
        for (let i = 0; i < props.length; i++) {
            const prop = props[i];
            const propName = prop.name;
            const supplyConfig = prop;
            if (isEmpty(propName))
                continue;
            let defaultValue = prop.default || undefined;
            const types = new Array(0).concat(prop.type).map((item) => item && item.toUpperCase()).filter(Boolean);
            if (isEffectArray(types) && !types.includes(type(prop.default || undefined).toUpperCase())) {
                defaultValue = undefined;
            }
            supplyConfig.assignCount = 0;
            supplyConfig.default = defaultValue;
            supplyConfig.types = types;
            if (prop.initProp)
                object[propName] = defaultValue;
            if (isNoEmpty(prop.default) && isEmpty(object[propName]))
                object[propName] = defaultValue;
            const propConfig = {};
            if (isNoEmpty(prop.configurable))
                propConfig.configurable = prop.configurable;
            if (isNoEmpty(prop.enumerable))
                propConfig.enumerable = prop.enumerable;
            if (isNoEmpty(prop.writable))
                propConfig.writable = prop.writable;
            if (isEffectObject(propConfig))
                propsConfig[propName] = propConfig;
            supplyConfigs[propName] = supplyConfig;
        }
        Object.defineProperties(object, propsConfig);
        const { set, ...rest } = handler;
        return new Proxy(object, {
            set(target, prop, newValue, receiver) {
                const supplyConfig = supplyConfigs[prop];
                const newValueType = type(newValue).toUpperCase();
                if (set)
                    return set(target, prop, newValue, receiver);
                if ((isEffectArray(supplyConfig.types) && !supplyConfig.types.includes(newValueType))
                    || (supplyConfig.required === true && isEmpty(newValue))
                    || (supplyConfig.once && supplyConfig.assignCount++ > 0)) {
                    return Reflect.set(target, prop, target[prop], receiver);
                }
                return Reflect.set(target, prop, newValue, receiver);
            },
            ...rest
        });
    }
    return object;
}

/**
 * @title arrayToTree
 * @description 数组转树
 * @param {object[]} array
 * @param {string} id
 * @param {string} pid
 * @returns {object[]}
 * @version 2.4.1
 */
function arrayToTree(array, id = 'id', pid = 'pid', child = 'children') {
    const list = deepClone(array);
    const result = [];
    const temp = {};
    // 先把全部id对应项 用temp 存起来
    for (let i = 0; i < list.length; i++)
        temp[list[i][id]] = list[i];
    for (let k = 0; k < list.length; k++) {
        if (temp[list[k][pid]] && list[k][id] !== list[k][pid]) {
            if (!temp[list[k][pid]][child]) {
                temp[list[k][pid]][child] = [];
            }
            temp[list[k][pid]][child].push(list[k]);
        }
        else
            result.push(list[k]);
    }
    return result;
}

export { EventEmitter, ObjectEntity, ProxyObject, arrayToTree, ascSort, asyncGuard, at, between, bind, chunk, clamp, compareNumber, compareString, compareValue, concat, copyWithin, cron, cronToChinese, cronToText, curry, dayOfYear, deadline, debounce, deepClone, delay, descSort, drop, dropRight, entries, equal, existKeys, fill, filter, flat, format, getDecimal, getIndex, getLength, getPathValue, guard, has, hide, inRange, includes, indexOf, intervalDate, isSameDate, like, logGroup, loop, memoize, nth, omit, once, pad, padEnd, padNumber, padStart, pageQuery, parse, pathObject2Object, pipe, replaces, reverseString, runFunc, select, selects, serialize, setValueByPath, sleep, spLength, stringify, throttle, timezone, toArray, toDate, toFirstLowerCase, toFirstUpperCase, toFloat$1 as toFloat, toLowerCase, toNumber, toPathObject, toPathValue, toString, toStrings, toThousands, toUpperCase, unique, vid, zip, zipObject };
//# sourceMappingURL=index.esm.js.map
